/**
 * Display formatting shared by the SPA, the API and the worker.
 * Kept dependency-free so it is safe in every runtime.
 */

import { UNKNOWN_UNIT } from './units';

const VULGAR_FRACTIONS: Array<[number, string]> = [
  [1 / 8, '⅛'],
  [1 / 4, '¼'],
  [1 / 3, '⅓'],
  [3 / 8, '⅜'],
  [1 / 2, '½'],
  [5 / 8, '⅝'],
  [2 / 3, '⅔'],
  [3 / 4, '¾'],
  [7 / 8, '⅞'],
];

/** Rounds to `dp` places, avoiding binary float drift (0.1 + 0.2). */
export function round(value: number, dp = 2): number {
  const factor = 10 ** dp;
  return Math.round((value + Number.EPSILON) * factor) / factor;
}

/** Renders a number with a vulgar fraction where one fits closely (1.5 → '1½'). */
function fractional(amount: number): string {
  const whole = Math.floor(amount);
  const fraction = round(amount - whole, 3);

  for (const [value, glyph] of VULGAR_FRACTIONS) {
    if (Math.abs(fraction - value) < 0.02) {
      return whole > 0 ? `${whole}${glyph}` : glyph;
    }
  }
  return String(round(amount, 2));
}

/** True when `value` lands on a quarter, so it renders as a tidy fraction. */
function isQuarterStep(value: number): boolean {
  return Math.abs(value * 4 - Math.round(value * 4)) < 1e-9;
}

/**
 * Formats a quantity. Count units and spoon measures read better as vulgar fractions
 * (1.5 → '1½'); mass and volume read better as decimals ('225 g').
 */
export function formatAmount(amount: number, unitNormalized: string): string {
  const rounded = round(amount, 2);
  if (Number.isInteger(rounded)) return String(rounded);
  return unitNormalized.startsWith('count:') ? fractional(rounded) : String(rounded);
}

/** Converts a normalised base amount back to a friendly display unit (1500 g → 1.5 kg). */
export function toFriendlyUnit(amountBase: number, unitNormalized: string): { amount: number; unit: string } {
  // Unitless legacy amounts render as a bare number rather than inventing a unit.
  if (unitNormalized === UNKNOWN_UNIT) {
    return { amount: round(amountBase, 2), unit: '' };
  }

  if (unitNormalized === 'g' && Math.abs(amountBase) >= 1000) {
    return { amount: round(amountBase / 1000, 2), unit: 'kg' };
  }

  if (unitNormalized === 'ml') {
    if (Math.abs(amountBase) >= 1000) {
      return { amount: round(amountBase / 1000, 2), unit: 'l' };
    }
    // Prefer spoons when the amount lands on a quarter spoon, so 22.5 ml reads as '1½ tbsp'
    // rather than an awkward millilitre figure.
    const tablespoons = amountBase / 15;
    if (amountBase >= 15 && isQuarterStep(tablespoons)) {
      return { amount: round(tablespoons, 2), unit: 'tbsp' };
    }
    const teaspoons = amountBase / 5;
    if (amountBase >= 5 && isQuarterStep(teaspoons)) {
      return { amount: round(teaspoons, 2), unit: 'tsp' };
    }
  }

  if (unitNormalized.startsWith('count:')) {
    return { amount: amountBase, unit: unitNormalized.slice('count:'.length) };
  }
  return { amount: round(amountBase, 2), unit: unitNormalized };
}

/** Full display string for a shopping-list line, e.g. '1½ tbsp' or '225 g'. */
export function formatQuantity(amountBase: number, unitNormalized: string): string {
  const { amount, unit } = toFriendlyUnit(amountBase, unitNormalized);
  const rounded = round(amount, 2);

  // Fractions suit spoons and countable items; decimals suit mass and volume.
  const wantsFraction =
    unit === 'tbsp' || unit === 'tsp' || unitNormalized.startsWith('count:');
  const text =
    Number.isInteger(rounded) || !wantsFraction ? String(rounded) : fractional(rounded);

  // `.trim()` so a unitless amount renders as '260', not '260 '.
  return `${text} ${unit}`.trim();
}

/** ISO-8601 duration ('PT15M', 'PT1H30M') → minutes. */
export function parseIsoDurationMinutes(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const match = /^P(?:(\d+)D)?T?(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?$/i.exec(iso.trim());
  if (!match) return null;
  const [, d, h, m] = match;
  const minutes = Number(d ?? 0) * 1440 + Number(h ?? 0) * 60 + Number(m ?? 0);
  return Number.isFinite(minutes) ? minutes : null;
}

export function formatMinutes(minutes: number | null | undefined): string {
  if (minutes == null) return '—';
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0 ? `${h} hr` : `${h} hr ${m} min`;
}

/**
 * Maps HelloFresh's numeric difficulty to a label.
 *
 * NOTE: 1 = Easy is confirmed from the rendered page; 2/3 are inferred and must be
 * confirmed against more recipes during Phase 1 (PLAN.md §6.3).
 */
export function difficultyLabel(value: number | null | undefined): 'Easy' | 'Medium' | 'Hard' | null {
  switch (value) {
    case 1:
      return 'Easy';
    case 2:
      return 'Medium';
    case 3:
      return 'Hard';
    default:
      return null;
  }
}
